import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {useCallback, useMemo, useState} from 'react'

import {LABELS} from '../../../constants/labels'
import {useItemPickerActivationAnalytics} from '../../../hooks/use-item-picker-activation-analytics'
import {IssueFieldMultiSelectValueTokens} from '../../fields/IssueFieldSingleSelectValueToken'
import type {
  ProjectMultiSelectFieldNode,
  ProjectMultiSelectFieldOptionNode,
  ProjectMultiSelectFieldValueNode,
} from '../project-item-section-fields-query-types'
import {FieldWrapper} from './FieldWrapper'
import {type ClearableFieldProps, getTokenStyle} from './Shared'
import {SingleSelectLeadingVisual} from './SingleSelectToken'

export type MultiSelectFieldOptionFragment = Pick<ProjectMultiSelectFieldOptionNode, 'id' | 'name' | 'color'>

export type MultiSelectFieldConfigFragment = Pick<ProjectMultiSelectFieldNode, 'id' | 'name' | 'multiSelectOptions'>

export type MultiSelectFieldFragment = Pick<ProjectMultiSelectFieldValueNode, 'id' | 'options'>

type MultiSelectFieldProps = ClearableFieldProps<MultiSelectFieldConfigFragment, MultiSelectFieldFragment>

export function MultiSelectField({
  viewerCanUpdate,
  itemId,
  field,
  value,
  onClearFieldValue,
  onUpdateFieldValue,
}: MultiSelectFieldProps) {
  const onItemActivation = useItemPickerActivationAnalytics('project_multi_select')
  const [showInput, setShowInput] = useState(false)
  const [filter, setFilter] = useState('')

  const fieldId = field.id
  const fieldName = field.name

  const allOptions = useMemo(() => {
    return [...field.multiSelectOptions]
  }, [field.multiSelectOptions])

  const valueOptions = useMemo(() => {
    return value?.options || []
  }, [value?.options])

  const selectedOptionIds = useMemo(() => new Set(valueOptions.map(option => option.id)), [valueOptions])

  const selectedItems = useMemo(
    () => allOptions.filter(option => selectedOptionIds.has(option.id)),
    [allOptions, selectedOptionIds],
  )

  const items = useMemo(() => {
    if (!filter) return allOptions
    return allOptions.filter(option => option.name.toLowerCase().includes(filter.toLowerCase()))
  }, [allOptions, filter])

  const onSelectionChange = useCallback(
    (selection: MultiSelectFieldOptionFragment[]) => {
      if (selection.length === 0) {
        onClearFieldValue(fieldId)
      } else {
        onUpdateFieldValue(fieldId, {
          id: `optimistic-field-value-${itemId}-${fieldId}`,
          field: {id: fieldId},
          options: selection.map(option => ({id: option.id, name: option.name, color: option.color})),
        })
      }

      setShowInput(false)
    },
    [fieldId, itemId, onClearFieldValue, onUpdateFieldValue],
  )

  const filterItems = useCallback((valueText: string) => {
    setFilter(valueText)
  }, [])

  const convertToItemProps = useCallback(
    (option: MultiSelectFieldOptionFragment): ExtendedItemProps<MultiSelectFieldOptionFragment> => {
      return {
        id: option.id,
        text: option.name,
        leadingVisual: () => <SingleSelectLeadingVisual inputColor={option.color} />,
        source: option,
      }
    },
    [],
  )

  const valueTokens =
    valueOptions.length > 0 ? (
      <IssueFieldMultiSelectValueTokens
        options={valueOptions.map(option => ({
          id: option.id,
          name: option.name,
          color: option.color,
        }))}
        getTooltipText={() => undefined}
        size="small"
        tokenClassName={getTokenStyle(fieldName)}
      />
    ) : null

  return (
    <>
      {!viewerCanUpdate ? (
        <FieldWrapper
          showInput={showInput}
          setShowInput={setShowInput}
          placeholder={LABELS.emptySections.noValue(fieldName)}
          canUpdate={false}
          name={fieldName}
          value={valueTokens}
        />
      ) : (
        <ItemPicker
          items={items}
          initialSelectedItems={selectedItems}
          filterItems={filterItems}
          getItemKey={option => option.id}
          convertToItemProps={convertToItemProps}
          placeholderText={LABELS.emptySections.singleSelectFilterPlaceholder}
          selectionVariant="multiple"
          onSelectionChange={onSelectionChange}
          onItemActivation={onItemActivation}
          renderAnchor={anchorProps => {
            return (
              <FieldWrapper
                showInput={showInput}
                setShowInput={setShowInput}
                placeholder={LABELS.emptySections.noValue(fieldName)}
                name={fieldName}
                anchorProps={{
                  ...anchorProps,
                  'aria-label': valueOptions.length > 0 ? `Edit ${fieldName}` : `Set ${fieldName}`,
                }}
                value={valueTokens}
              />
            )
          }}
          height="large"
          improvedNoMatchAccessibility={items.length === 0 && filter.length > 0}
          noMatchMessage={
            items.length === 0 && filter.length > 0
              ? {
                  title: 'No option found',
                  variant: 'empty',
                  body: 'Try searching with a different query for results.',
                }
              : undefined
          }
          cssAnchorPositioningSettings={{fallbackStrategy: 'opposite-side'}}
        />
      )}
    </>
  )
}
